import { useContext } from 'react'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { ModalKey, ModalContext } from 'components/Modals/ModalContext'
import { FilterWorkoutByWorkoutLength } from 'functions/workoutUtils'
import kinesisLogo from 'media/kinesis_logo.png'

import GroupIndividualWorkoutCompletionLinearFill from 'components/Training/TrainingCalendar/GroupIndividualWorkoutCompletionLinearFill'

const DISPLAY_WORKOUT_IDS = false

export default function KinesisWorkoutCalendarItem({ 
    propProfileType = 'individual',
    kinesisUserGroupId = null,
    userId = null,
    kinesisProfileDetails = null,
    kinesisGroupIndividualWorkoutCalendarItemData = [],
    timescale = 'week',
    isDateBeforeClientISOWeek = false,
    isDateInClientISOWeek = false,
    isDateAfterClientISOWeek = false,
    isDateBeforeClientDate = false,
    isDateClientDate = false,
    isDateAfterClientDate = false,
    kinesisWorkoutCalendarItemData = null,
    displayStatusText = false,
    displayWorkoutStructure = false,
    isDraggable = false,
    isBeingDragged = false,
    OnDragStart = () => {},
    OnDragEnd = () => {},
    clientDate = null,
    OnWorkoutOneOffOverridesSaved = () => {}
}) {

    const { exerciseHashMap } = useGetExercises()

    const { ShowModal } = useContext(ModalContext)

    const GetTailwindCssClassName = () => {

        if (timescale === 'block') {
            return GetBlockTimescaleTailwindCssClassName()
        }

        if (timescale === 'week') {
            return GetWeekTimescaleTailwindCssClassName()
        }

        return ''
    }

    const GetBlockTimescaleTailwindCssClassName = () => {

        var className = 'py-0.5 px-1 rounded-md border-l-[3px] 2xl:border-l-[4px] shadow cursor-pointer hover:shadow-md'

        // Group profile

        if (propProfileType === 'group') {

            return className + ' border-orange-400 bg-orange-100'

        }

        // Individual profile

        if (propProfileType === 'individual') {

            // Past weeks

            if (isDateBeforeClientISOWeek) {

                // Incomplete

                if (kinesisWorkoutCalendarItemData.completed === 0) return className + ' border-red-400 bg-red-100'
                
                // Complete

                if (kinesisWorkoutCalendarItemData.completed === 1) return className + ' border-green-400 bg-green-100'

            }

            // Present week

            if (isDateInClientISOWeek) {

                // Incomplete

                if (kinesisWorkoutCalendarItemData.completed === 0) return className + ' border-orange-400 bg-orange-100'
                
                // Complete

                if (kinesisWorkoutCalendarItemData.completed === 1) return className + ' border-green-400 bg-green-100'

            }

            // Future weeks

            if (isDateAfterClientISOWeek) {

                return className + ' border-orange-400 bg-orange-100'

            }

        }

        return className
    }

    const GetWeekTimescaleTailwindCssClassName = () => {
        var className = 'bg-white border-t-[3px] border-gray-300 w-full p-1 rounded-md shadow cursor-pointer hover:shadow-md'

        // Group profile: always orange, same as the block timescale (the group's own workout is never completed)

        if (propProfileType === 'group') {
            className = className.replace('bg-white', 'bg-orange-50')
            className = className.replace('border-gray-300', 'border-orange-300')
            return className
        }

        // Complete Styling (green)

        if (kinesisWorkoutCalendarItemData.completed === 1) {
            className = className.replace('bg-white', 'bg-green-50')
            className = className.replace('border-gray-300', 'border-green-300')
            return className
        }

        // Incomplete Styling

        if (kinesisWorkoutCalendarItemData.completed === 0) {

            // Workout in the past (red)

            if (isDateBeforeClientDate) {
                className = className.replace('bg-white', 'bg-red-50')
                className = className.replace('border-gray-300', 'border-red-300')
                return className
            }

            // Workout in the present (orange)
            
            if (isDateClientDate) {
                className = className.replace('bg-white', 'bg-orange-50')
                className = className.replace('border-gray-300', 'border-orange-300')
                return className
            }

            // Workout in the future (orange)

            if (isDateAfterClientDate) {
                className = className.replace('bg-white', 'bg-orange-50')
                className = className.replace('border-gray-300', 'border-orange-300')
                return className
            }

        }

        return className
    }

    const GetStatusText = () => {

        // Complete status text

        if (kinesisWorkoutCalendarItemData.completed === 1) return 'Completed'

        // Incomplete status text

        if (kinesisWorkoutCalendarItemData.completed === 0) {

            if (isDateBeforeClientDate) return 'Incomplete'

            if (isDateClientDate) return 'Preview'

            if (isDateAfterClientDate) return 'Preview'

        }

        return "Unknown"
    }

    // V5 slots keep their assigned set units on each set, in the unit of the slot's exercise (reps or seconds)

    const GetSlotSetUnitsLabel = (slot) => {
        const setUnitsAssigned = slot.sets?.[0]?.setUnitsAssigned ?? 0
        const setUnit = exerciseHashMap[slot.exerciseId]?.setUnit ?? 'reps'

        if (setUnit === 'seconds') {
            return `${setUnitsAssigned} Sec`
        }

        return `${setUnitsAssigned} ${setUnitsAssigned === 1 ? 'Rep' : 'Reps'}`
    }

    // Completed workouts show how much of each slot was done: set units completed across all its sets over set units assigned
    // (skipped and unsaved sets count as 0), so sets are accounted for without more room than the assigned set units label

    const GetSlotCompletedPercentLabel = (slot) => {
        const sets = slot.sets ?? []
        const setUnitsAssigned = sets.reduce((total, set) => total + (Number(set.setUnitsAssigned) || 0), 0)
        const setUnitsCompleted = sets.reduce((total, set) => total + (set.completed === 1 ? (Number(set.setUnitsCompleted) || 0) : 0), 0)

        if (setUnitsAssigned === 0) {
            return '-'
        }

        return `${Math.round((setUnitsCompleted / setUnitsAssigned) * 100)}%`
    }

    // Group individual profile: an athlete currently in a group. Read only (their program is managed by the group), but their workouts can be run
    const isGroupIndividualProfile = propProfileType === 'individual' && kinesisProfileDetails?.kinesisUserGroupId != null

    const GetWorkoutV5IncompletePreviewModalData =(displayStartWorkoutButton) => {

        // Coaches can make one-off edits to workouts that haven't been started or completed on a profile's own calendar (not the program playground preview).
        // Group workouts can always be edited: the group's workout is always the latest version, and only the athletes' completed copies are set in stone

        const isEditable = (
            (kinesisUserGroupId !== null || userId !== null)
            && isGroupIndividualProfile === false
            && (
                propProfileType === 'group'
                || (kinesisWorkoutCalendarItemData.completed !== 1 && kinesisWorkoutCalendarItemData.started !== 1)
            )
        )

        return {
            key: ModalKey.WorkoutV5IncompletePreview,
            size: 'workout-summary',
            kinesisUserGroupId: kinesisUserGroupId,
            userId: userId,
            workout: kinesisWorkoutCalendarItemData,
            displayStartWorkoutButton: displayStartWorkoutButton,
            profileType: propProfileType,
            clientDate: clientDate,
            isEditable: isEditable,
            OnWorkoutOneOffOverridesSaved: OnWorkoutOneOffOverridesSaved
        }
    }

    const OnWorkoutCalenderItemClicked = () => {

        var modalData = null

        // Group profile

        if (propProfileType === 'group') {

            // Past

            if (isDateBeforeClientISOWeek) {
                // Do not allow modal for past group workous. TODO: Fix this
                return
            }

            // Present

            if (isDateInClientISOWeek) {
                modalData = GetWorkoutV5IncompletePreviewModalData(true)
            }

            // Future

            if (isDateAfterClientISOWeek) {
                modalData = GetWorkoutV5IncompletePreviewModalData(false)
            }

        }

        // Individual profile

        if (propProfileType === 'individual') {

            // Past

            if (isDateBeforeClientISOWeek) {
                modalData = {
                    key: ModalKey.WorkoutV5CompletedSummary,
                    size: 'workout-summary',
                    workout: kinesisWorkoutCalendarItemData
                }
            }

            // Present

            if (isDateInClientISOWeek) {

                // Complete

                if (kinesisWorkoutCalendarItemData.completed === 1) {
                    modalData = {
                        key: ModalKey.WorkoutV5CompletedSummary,
                        size: 'workout-summary',
                        workout: kinesisWorkoutCalendarItemData,
                        isDeletable: userId !== null && (kinesisWorkoutCalendarItemData.kinesisUserGroupId == null || isGroupIndividualProfile), // A group individual's copy is redone through the group's program
                        userId: userId,
                        clientDate: clientDate,
                        OnWorkoutDeleted: OnWorkoutOneOffOverridesSaved // Reloads the training calendar
                    }
                }

                // Incomplete

                if (kinesisWorkoutCalendarItemData.completed === 0) {
                    modalData = GetWorkoutV5IncompletePreviewModalData(true)
                }

            }

            // Future

            if (isDateAfterClientISOWeek) {
                modalData = GetWorkoutV5IncompletePreviewModalData(false)
            }

        }

        if (modalData !== null) {
            ShowModal(modalData)
        }
    }

    const OnWorkoutCalendarItemDragStart = (e) => {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', kinesisWorkoutCalendarItemData.name) // Firefox requires data to be set to start a drag
        setTimeout(OnDragStart, 0) // Defer so the browser captures the drag image before the original item is hidden
    }

    if (
        Object.keys(exerciseHashMap).length === 0
        || kinesisWorkoutCalendarItemData === null
    ) {
        return null
    }

    // A completed workout trained at a selected length shows only the slots in it in week mode (mobility workouts have no selected length)
    const selectedWorkoutLength = kinesisWorkoutCalendarItemData.selectedWorkoutLength ?? null
    const displayedWorkout = (
        timescale === 'week'
        && kinesisWorkoutCalendarItemData.completed === 1
        && selectedWorkoutLength !== null
    ) ? FilterWorkoutByWorkoutLength(kinesisWorkoutCalendarItemData, selectedWorkoutLength) : kinesisWorkoutCalendarItemData

    return (
        <div 
            className={`
                ${GetTailwindCssClassName()} 
                ${isDraggable ? 'active:cursor-grabbing' : ''} 
                ${isBeingDragged ? 'hidden' : ''}
            `}
            onClick={OnWorkoutCalenderItemClicked}
            draggable={isDraggable}
            onDragStart={isDraggable ? OnWorkoutCalendarItemDragStart : undefined}
            onDragEnd={isDraggable ? OnDragEnd : undefined}
        >
            <div className='flex items-center gap-x-0.5'>
                <img src={kinesisLogo} alt='Kinesis Logo' className='mt-[1px] h-4 w-4' />
                
                <div className='text-xxs 2xl:text-xs font-medium truncate flex-1'>
                    {kinesisWorkoutCalendarItemData.name}
                </div>
            </div>
            
            {DISPLAY_WORKOUT_IDS && (
                <p className='text-xxs'>{kinesisWorkoutCalendarItemData.workoutConfigId}</p>
            )}

            {propProfileType === 'group' && (
                <GroupIndividualWorkoutCompletionLinearFill
                    isDateAfterClientISOWeek={isDateAfterClientISOWeek}
                    kinesisWorkoutCalendarItemData={kinesisWorkoutCalendarItemData}
                    kinesisGroupIndividualWorkoutCalendarItemData={kinesisGroupIndividualWorkoutCalendarItemData}
                    kinesisProfileDetails={kinesisProfileDetails}
                />
            )}

            {displayStatusText && (
                <div className='pl-0.5 text-gray-500 text-xxs 2xl:text-xs'>
                    {GetStatusText()}
                </div>
            )}

            {displayWorkoutStructure && displayedWorkout.phases.map((phase, index) => (
                <div 
                    key={index}
                    className='p-1 space-y-0.5'
                >
                    <div className='flex items-center gap-1'>
                        <p className='text-xxs 2xl:text-xs font-bold text-gray-600 flex-1 truncate'>{phase.name}</p>
                        <p className='hidden 2xl:block text-xs font-bold truncate'>{phase.sets} {phase.sets === 1 ? 'Set' : 'Sets'}</p>
                    </div>

                    {DISPLAY_WORKOUT_IDS && (
                        <p className='text-xxs'>{phase.exerciseBlockId}</p>
                    )}

                    {phase.slots.map((slot, index) => (
                        <div 
                            key={index}
                            className='flex items-center gap-1'
                        >
                            <p className='flex-1 text-xxs 2xl:text-xs text-gray-600 truncate'>{exerciseHashMap[slot.exerciseId].name}</p>
                            <p className='hidden 2xl:block text-xs text-gray-600 truncate'>
                                {kinesisWorkoutCalendarItemData.completed === 1 ? GetSlotCompletedPercentLabel(slot) : GetSlotSetUnitsLabel(slot)}
                            </p>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    )
}